import actionTypes from 'src/modules/shared/context/actionTypes';
import api from 'src/utils/api';
import { setAlert } from 'src/typescript/components/alert/actions';
import { showSignupSurveySocial, showExistingLoginSocialModal,
  closeModal }  from 'src/modules/shared/modal/actions';
import { trackEvent, trackAccountCreatedEvent, trackEventForPartner } from 'src/utils/analytics';
import { trackFootfallEvent } from 'src/utils/footfall';
import { getBranch } from 'src/utils/cookie';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import { followCommunity } from 'src/modules/community/shared/actions';
import * as log from 'loglevel';
import i18n from 'src/utils/translate';

const STATUS_CODES = {
  NO_ACCOUNT: '40400010',
  EXISTING_ACCOUNT: '40400012',
  INVALID: '40400013',
  WRONG_DETAILS: '40011221',
};

export const setServerSession = (sessionId) => {
  return {
    type: actionTypes.SET_SERVER_SESSION,
    sessionId,
  };
};

export const unsetServerSession = () => {
  return {
    type: actionTypes.UNSET_SERVER_SESSION,
  };
};

export const login = ({ password, username, staySignedIn = true, onSuccess = () => {} }) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.LOGIN });
    try {
      const response = await api.auroraPost('session', { password, username, staySignedIn });
      const { data: { status } } = response;

      if (status && !status.success) {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
        dispatch(setAlert(status.message, 'danger', false));
        throw new Error(status.message);
      }

      dispatch(closeModal());
      onSuccess();
      await dispatch({ type: actionTypes.LOGIN_SUCCESS });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.LOGIN_FAILURE });
    }
  };
};

// Do not add status.success check & error handling like on login(), it causes social signup/login to fail
export const loginWithSocial = (socialLoginData, socialType, onSuccess, surveyProps = {}) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.SOCIAL_LOGIN });

    try {
      const { data: { status } } = await api.auroraPost('session/oauth', socialLoginData);

      if (status && status.success) {
        dispatch(closeModal());
        trackEvent('Account', `Logged in via ${socialType}`);
        onSuccess();
        await dispatch({ type: actionTypes.SOCIAL_LOGIN_SUCCESS });
      } else {
        handleApiSocialLoginResponse(status, socialLoginData, dispatch, onSuccess, surveyProps);
      }
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.SOCIAL_LOGIN_FAILURE });
    }
  };
};

const handleApiSocialLoginResponse = (status, authData, dispatch, onSuccess, surveyProps) => {
  switch (status.responseCode) {
    case STATUS_CODES.NO_ACCOUNT:
      return dispatch(showSignupSurveySocial(authData, onSuccess, surveyProps));
    case STATUS_CODES.EXISTING_ACCOUNT:
      // if your email is already associated with an HU account, this modal appears and attempts to link the social accounts
      return dispatch(showExistingLoginSocialModal(authData, onSuccess));
    case STATUS_CODES.INVALID:
      return dispatch(setAlert(i18n.t('Invalid Login!')));
    case STATUS_CODES.WRONG_DETAILS:
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
      return dispatch(setAlert(status.message, 'danger', false));
  }
};

export const signup = ({ password, username, email, accessToken, isVerified, isCommunitySignup }, signupContext = {}) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.SIGN_UP });

    try {
      const { context: { currentCommunity } } = getState();
      const response = await api.auroraPost('profile', { password, username, email, accessToken, isVerified });
      const { data: { status, payload } } = response;

      if (status && !status.success) {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
        dispatch(setAlert(status.message, 'danger', false));
        throw new Error(status.message);
      }

      if (!payload) {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
        dispatch(setAlert(i18n.t('There was a problem. Please refresh the page and try again.'), 'danger', false));
        throw new Error(`User signup: payload undefined. status.message: ${status.message}`);
      }

      await dispatch({ type: actionTypes.SIGN_UP_SUCCESS, signupContext });
      if (!!currentCommunity && !currentCommunity.isArchived) {
        await dispatch(followCommunity(currentCommunity, payload.user.id, null, true));
      }

      // if its not a community signup, so from a community page or following a community on community search, we automatically follow them to blog
      if (!isCommunitySignup) {
        const { data } = await api.getPublic('communities/blog');
        await dispatch(followCommunity(data, payload.user.id, null));
      }

    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.SIGN_UP_FAILURE });
    }
  };
};

export const signupWithSocial = (socialSignupData, socialType, isCommunitySignup) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.SOCIAL_SIGN_UP });
    try {
      const { context: { currentCommunity } } = getState();
      const response = await api.auroraPost('profile', socialSignupData);
      const { data: { payload, status } } = response;

      if (status && !status.success) {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 0-1 arguments, but got 2.
        throw new Error(status.message, status.message);
      }

      const user = payload.user;
      await dispatch({ type: actionTypes.SOCIAL_SIGN_UP_SUCCESS });

      const branch = getBranch();
      trackFootfallEvent(footfallEventTypes.SIGNED_UP);
      trackEvent('Account', `Created ${socialType} (${branch})`); // UA
      trackAccountCreatedEvent(`Created ${socialType} (${branch})`); // GA4

      if (currentCommunity) {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 5 arguments, but got 3.
        trackEventForPartner(currentCommunity, 'Account', 'Created');
        await dispatch(followCommunity(currentCommunity, user.id, null, true));
      }

      if (!isCommunitySignup) {
        const { data } = await api.getPublic('communities/blog');
        await dispatch(followCommunity(data, user.id, null));
      }

    } catch (ex) {
      log.error(ex);
      dispatch(setAlert(ex.message));
      return dispatch({ type: actionTypes.SOCIAL_SIGN_UP_FAILURE });
    }
  };
};

export const logout = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.LOGOUT });

    try {
      const response = await api.auroraDel('session');
      const { data: { status } } = response;

      if (status && !status.success) {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
        dispatch(setAlert(status.message, 'danger', false));
        return dispatch({ type: actionTypes.LOGOUT_FAILURE });
      }

      dispatch({ type: actionTypes.LOGOUT_SUCCESS });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.LOGOUT_FAILURE });
    }
  };
};

export const isUsernameAvailable = (username) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.CHECK_USERNAME });
    try {
      const { data: { payload } } = await api.auroraGet(`profile/check-username?username=${encodeURIComponent(username)}`);
      return dispatch({
        type: actionTypes.CHECK_USERNAME_SUCCESS,
        isAvailable: payload && payload.isAvailable,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.CHECK_USERNAME_FAILURE, isAvailable: false });
    }
  };
};

export const isEmailAvailable = async (email) => {
  try {
    const { data: { payload } } = await api.auroraGet(`profile/check-email?email=${encodeURIComponent(email)}`);
    return payload && payload.isAvailable;
  } catch (ex) {
    log.error(ex);
    return false;
  }
};
